/**
 * Native ReAct executor assembly.
 *
 * Creates a run-scoped native runtime that reuses hardened tool implementations
 * behind a synthetic internal step.
 */

import { MAX_EXECUTOR_ITERATIONS } from "./executorLimits";
import { withChatHistoryGuidance } from "./chatHistoryContext";
import {
  annotationReadResolvesBlockedOperation,
  blockedOperationExactlyMatches,
  buildExecutorSkillAssembly,
  ingestResearchToolResult,
  granularRecoveryKey,
  isConfirmedWriteResult,
  isReplayGuardedWriteToolCall,
  isStatefulToolCall,
  narrowGranularRemediationCells,
  recoverAmbiguousGranularRangeWrite,
  resolveWorksheetColumnRoute,
  isDirectIdLookupSourceRestricted,
  shouldCheckpointBlockedWrite,
  stableWriteFingerprint,
  processApplyTemplateOutcomes,
  withApplyTemplateOutcomeCapture,
  writeOverlapsBlockedOperation,
} from "./executorRunSupport";
import type {
  ExecutorBlockedOperation,
  ExecutorRunEvent,
  ExecutorRunnerConfig,
} from "./executorRunTypes";
import {
  detectIdLookupRoutingIntent,
  detectIdLookupWorksheetWorkload,
  type IdLookupRoutingIntent,
} from "./idLookupRoutingPolicy";
import { resolveIdLookupAvailability } from "./idLookupMode";
import { ID_LOOKUP_TOOL_NAME } from "./idLookupTool";
import type { IdLookupBatchProgress } from "./idLookupBatch";
import { ASK_USER_TOOL } from "./userQuestion";
import {
  detectWorksheetCompanyList,
  detectWorksheetCompanyListFromContext,
} from "./worksheetCompanyList";
import {
  createExecutorToolRuntime,
  type ExecutorRunAccumulators,
  type ExecutorRunSessionState,
  type ExecutorToolRuntimeContext,
  type RuntimeToolExecutor,
} from "./executorToolRuntime";
import { FactSetFormulaFormatRegistry } from "./factsetFormulaFormatRegistry";
import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type { ExecutorPlanState, ExecutorRunResult, ExecutorUnifiedRunResult } from "./types";
import {
  COMPLETE_RUN_TOOL_NAME,
  runBoundedNativeReactHarness,
  type NativeReactHarnessConfig,
} from "./executorNativeReactHarness";
import { ExecutorWorkbookHandoffError } from "./executorWorkbookTools";
import { serializeNativeWorkbookBootstrap } from "./executorNativeWorkbookContext";
import { getWorksheetsAsBase64 } from "./excelHostOps";
import {
  buildExcelWorkbookInputFileItem,
  decodeWorkbookBase64ByteLength,
  isExcelWorkbookInputFileError,
  MAX_EXCEL_WORKBOOK_INPUT_FILE_BYTES,
} from "./executorNativeWorkbookInputFile";
import { withCredentials } from "../shared/apiClient";
import { fetchWithRetry } from "../shared/utils/http";
import { getExecutorAdapter, type AgentTurn, type AgentTurnResult } from "./providers";
import { resolveEffectiveExcelExecutorModel } from "./executorModelSelection";
import type { ExecutorModelConfig } from "./providers/registry";
import type { ExecutorProviderAdapter } from "./providers/types";
import {
  executeToolCallDetailed,
  type ToolCallContext,
  type ToolCallDetailedResult,
} from "./toolRegistry";
import {
  buildExcelUnifiedAgentGenerationMetadata,
  postExcelLangfuseObservation,
  withOptionalExcelLangfuseMetadata,
} from "./langfuseTrace";
import { postExcelToolObservation } from "./langfuseToolObservation";
import { LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS } from "./services/factset/spreadsheetIntents";
import {
  isAnyResearchToolName,
  isResearchToolName,
  type FinsterStreamSubEvent,
} from "../shared/api/finster/queryTool";
import { appendSavedContextRule, prependSavedContext } from "../shared/api/finster/savedContext";
import { getFinsterQueryToolInstruction } from "./finsterQueryTool";
import { extractCitedValuesById, normalizeResearchCitationsForModel } from "./citationNotes";
import { SET_CITATIONS_TOOL } from "./executorCitationTools";
import type { ExcelAgentSource } from "./agentTimeline";
import type { ApplyTemplateOutcome } from "./templateTools";
import { executeFactSetFormatSafetyPass, NATIVE_SET_RANGE_VALUES_TOOL } from "./executorWriteTools";

/** Stable internal step used by all native-mode write calls. */
export const NATIVE_RUN_STEP_ID = "native_run";

export const COMPLETE_RUN_TOOL: ChartCapabilityToolDef = {
  name: COMPLETE_RUN_TOOL_NAME,
  description:
    "End the native workflow. First write the final user-facing explanation as assistant text in the same turn, covering what you did, what worked, what did not, why, and what the user should examine next when relevant. Then call complete_run with no arguments and no other tool calls.",
  inputSchema: {
    type: "object",
    properties: {},
    required: [],
    additionalProperties: false,
  },
  strict: true,
};

/**
 * Legacy native operational instructions retained for compatibility and
 * focused safety-contract tests. The live native prompt is assembled by
 * {@link buildNativeReactSystemPrompt} and does not include this block.
 */
export const NATIVE_REACT_SYSTEM_PROMPT = `
You are the native ReAct execution agent for the Finster Excel add-in.
Reason from the user's request, inspect the live workbook with tools, act, and
verify the observed result before replying.

The initial workbook bootstrap is metadata only. It contains no trustworthy
cell values or formulas. Use list_sheets and targeted read_range calls for the
smallest ranges needed by the request. Do not read every sheet or an entire
large used range by default. If sheet inventory metadata was truncated, call
list_sheets before deciding scope. When a sheet has no usedRange metadata, its
used-range shape is unknown, not blank. Do not call read_range with a null address
or guess a broad range for that sheet; call list_sheets first.

There is one internal run step with id "${NATIVE_RUN_STEP_ID}". Pass that exact
stepId to every tool schema that requires one. The step exists only to retain
write validation and must not be described to the user as a plan.

Workbook changes must be grounded in tool observations. After a write, read the
changed range back before claiming completion. Never claim a host mutation that
the tool result did not confirm. Apply formatting only to the cells that require
it. Do not format entire rows or columns unless the user explicitly requests it.

Continue using tools while work remains. Assistant text communicates progress
and does not end the workflow. When the work is done, call complete_run alone
with the final user-facing answer and honest outcome. Do not expose tool names,
internal step ids, or hidden reasoning.
`.trim();

/**
 * Assemble native instructions from the banker persona, mandatory hygiene
 * documents, source scope, and chat-history safety policy. Optional capability
 * guidance belongs to the corresponding native tool definitions.
 *
 * The banker persona leads the prompt, followed by the complete mandatory
 * formatting, structure, data/formula, and QA hygiene documents. Specialist
 * modeling documents remain opt-in through `get_skill`.
 */
export function buildNativeReactSystemPrompt(
  config: ExecutorRunnerConfig,
  _routing: NativeToolRouting,
  now: Date = new Date()
): string {
  const date = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(now);
  const bankerOperatingStandard = `

# Your Identity and Role
You are an intelligent investment banker and a specialist in Excel specific banker workflows.
You perform investment-banking knowledge work and produce thorough, world class client-ready output
which is complete, accurate, thorough and formatted to banker standard and expectations.
Bear that in mind in every query you answer and every decision you make.

# Guiding Principle
Before acting, read through the given brief, properly understand the semantics of the given workbook, the sheets, the formulae,
what each table or range is trying to do, just the way a banker would think and reason about it.
Decompose the given underlying ask based on its difficulty to make sense of the requested deliverable, just like a strong investment banker would do.
Then think through all the knowledge search, read, write and edit tools you have and how you would sequence them to solve the problem and complete
the user's request. When you have a clear understanding of the given workbook, the problem you need to solve for the user, and your approach to it,
make sure you communicate your thinking and approach to the user before proceeding on with executing your solution.
While working through the problem, at each and every decision point, your guiding principle should always be to ask yourself
what a strong investment banker would do: when understanding a given notebook, searching for information, figuring out a failure,
editing, building or formatting a workbook, following the style and
customizations for an org and user, deciding whether to edit something easily or build from scratch, cross-checking analyses, reviewing and deciding
whether it is client-ready.

# Goal - Non-negotiable
Your goal is to understand and deliver a user request to completion. You should not stop before
you have either completed the brief to the standard expected by the user or you are blocked and need
some specific information on something which you are unable to find yourself. Nothing else should or can
make you stop your work. At the end of every piece of work, you should deliver an explanation of what you did,
what is complete, what needs further looking into and why.
It is critical that you do not stop when hitting failures. Instead, rethink the approach, go back to the drawing
board, and investigate further when you have not properly solved the user’s problem. That is non-negotiable.

# Today's Date
Today is ${date}. Use this date when the request depends on "today", "latest",
or another relative period.

# Current workbook snapshot

When a workbook file is attached, it is an initial snapshot of the pinned workbook captured before the first model turn. It is not refreshed or re-attached on later turns.

Use the snapshot actively to:
- understand the workbook's initial purpose, structure, formulas, dependencies, and formatting;
- develop the initial strategy for the user's request; and
- choose the best first actions.

The snapshot is read-only context. Make workbook changes only through the Excel tools. After any workbook change, use live Excel reads and range snapshots to reassess the latest draft and remaining work. Use live Excel reads whenever exact current values, formulas, formatting, or write targets must be confirmed. If the initial snapshot and a live tool result differ, treat the live tool result as authoritative.

If no workbook snapshot is attached, continue using the available Excel inspection tools.

# Visual range snapshots

Use \`get_snapshot\` when the task depends on how a worksheet actually renders:
- before visual-dependent edits when matching or reviewing layout, hierarchy, spacing, alignment, colours, or formatting;
- after material presentation changes as part of the separate visual QA pass; and
- across multiple targeted ranges or sheets when the relevant output does not fit clearly in one image.

For a full-sheet review, call \`list_sheets\` first and use the worksheet's used range:
- pass \`address: null\` when the full used range fits in one readable snapshot;
- if the tool reports that the used range is too large, divide the entire used range into contiguous row or column bands and snapshot every band rather than sampling only the first visible area;
- include or repeat title/header rows and nearby labels when needed to preserve visual context across bands; and
- inspect additional close-up ranges wherever a full-range image makes text or formatting too small to judge.

Before finishing a workbook with presentation changes, visually review every materially changed output sheet and every occupied area affected by the work. A successful write or formatting result is not visual proof. Repair defects, then take a fresh snapshot of the repaired area.

A visual snapshot helps you judge presentation, but it does not prove numerical correctness. \`read_range\` remains authoritative for exact values, formulas, and number-format codes. Use both when reviewing substantive model output. Do not call \`get_snapshot\` unnecessarily for a purely numerical task with no visual decision.

# Strategic Problem Solving and Delivery

Whenever planning before starting a task or in the middle of substantive work, always develop and maintain a strategy covering three questions:

1. What problem is the user actually asking you to solve?
2. What is the best way to solve it completely and efficiently?
3. What is the best way to present the result for its intended audience and use?

Keep your strategy in mind whenever solving any task. Scale the depth of this process to the task. A simple request may require only a brief assessment; a complex workbook, analytical exercise, or research workload requires deeper inspection and a more explicit strategy. This is a reasoning discipline, not a deterministic routing step.

At each material decision, apply the same test a strong investment banker would apply: a failed search, whether to edit or rebuild, how to handle an organization style, and whether the current evidence is good enough to write down. Do not wait until the end to make that judgement.

Then repeat the loop below on whatever remains until the job is done. A review that finds a material gap is the start of the next pass, not a reason to stop. Stop only when the review finds nothing material left, or a specific fact cannot be found after a genuinely different attempt.

## 1. Understand the problem and available evidence

Establish:

- the user's objective, intended audience, and requested deliverable;
- the relevant workbook purpose, structure, sheets, ranges, tables, row grain, and existing conventions;
- what each relevant sheet, range, and formula is doing, and how those formulas connect across worksheets;
- the complete relevant population when filtering, ranking, grouping, reconciliation, or coverage matters;
- the ranking or filtering criteria governing the requested output;
- the facts already available or derivable from the workbook;
- the information that genuinely requires external research;
- material ambiguities, assumptions, constraints, and completion criteria.

When relevant workbook evidence is not already available, inspect the workbook before substantive external research or modification. Read proportionally: use metadata, headers, representative rows, formulas, and targeted ranges to understand structure, but inspect the complete relevant population whenever exact coverage matters.

Never treat a small sample as evidence about unseen workbook content. Do not infer the contents of workbook cells or populations you have not inspected. Never ask a research agent to inspect, filter, deduplicate, or interpret workbook data it cannot see.

A task that is conversational or independent of workbook contents may not require workbook inspection.

## 2. Design the best solution

Think through the complete path from the available evidence to the required deliverable.

Determine:

- which facts can be read, calculated, transformed, or inferred locally;
- which facts require formulas, structured data, filings, broader research, or specialist workflow guidance;
- which tools are best suited to each part of the problem;
- which steps are independent and can run in parallel;
- which steps are dependent and must be sequenced;
- where validation, reconciliation, or review must occur;
- how to avoid unnecessary work while preserving completeness and quality.

Select tools after understanding the problem. Do not choose a workflow merely because a tool is available. Based on the tools available your guiding principle is how a banker would think about the problem and strategy to solve it. Apply that.

When a fact has to be found, first decide where a banker would look for that kind of fact. A standard market or company figure, a company identifier, a filing disclosure, a transcript comment, and a fact that lives in news or on the web are different places. Search the place that fits the fact.

A miss tells you about that attempt, not about every place the fact could live. If a filing or document search does not contain a figure, ask whether a data-service formula or a broader source would. If an identity lookup does not return a requested field, such as a parent, ticker, or entity, ask whether research or the web would. If a formula cannot produce a disclosed breakdown, ask whether a filing or another cited source would. Change the place, the question, or both. Do not repeat the same search and treat the fact as unavailable. Leave it unresolved only after the places a banker would still try have been tried, or the user has restricted the work to a source that does not contain it. A user-selected source allowlist still bounds where you may look.

For large populations or research workloads, design the work rather than treating it as an arbitrary collection of calls. Establish the population, grain, stable identifiers, requested fields, source requirements, and completion conditions. Normalize and deduplicate workbook records while preserving their source-row locations. Then design the specific targeted research you would need to do.

Where appropriate, separate:

- workbook evidence and calculations;
- broad screening or structured enrichment;
- deeper entity-specific qualitative research;
- ranking, synthesis, and presentation.

Design deliberate stages such as screen, shortlist, analyze, investigate, rank, and produce when the deliverable permits population reduction before expensive research. Research only the entities and fields needed to satisfy the brief.

Each research query must name:

- the entity or entities;
- the time period(s);
- the exact fact/metric/table being asked for;
- the evidence to return, including the resolved value, the source link or file, and the source date;
- the specific format of the returned answer.

Choose how wide each research query is from similarity, complexity, expected response size, available sources, citation requirements, and the quality of results already in hand. Queries spanning multiple entities or time periods take longer. So batch your queries to the appropriate size resolve them faster.

After each meaningful stage, assess coverage and quality. Reuse successful evidence from previous stages, investigate the specific gaps, and revise your strategy when new information changes the problem.

## 3. Design the output and presentation

Before building or editing the final output, determine how a strong investment banker would present it.

Consider:

- the intended reader and decision the output must support;
- whether the result belongs in an existing range, a new table, a new worksheet, formulas, commentary, or a combination;
- the clearest hierarchy, ordering, grouping, labels, columns, summaries, and supporting detail;
- the appropriate level of precision, units, dates, signs, assumptions, and explanatory notes;
- how to preserve the workbook's existing visual language and user or organization style;
- how the output should distinguish inputs, calculations, researched facts, conclusions, caveats, and unresolved items;
- how raw/source data, calculations, narrative evidence, and final presentation outputs should be separated and linked;
- what review a senior banker or client would expect before receiving the work.

Ground every externally sourced hardcoded fact, value, identity, or claim written
to the workbook in matching evidence through a citation. Generic headers, labels,
and units can stay uncited when they are presentation structure rather than
retrieved information. If one of those cells itself contains retrieved
information, cite it. Formula cells and blank cells remain uncited.

The \`factset_ai_query\` result contains a cited \`answer\` and normalized
\`citations\` metadata. After writing a supported hardcoded value or substantive
claim, call \`set_citations\` and copy exactly one complete matching metadata
record onto that cell. Do not pass citation IDs to \`set_citations\`, do not
hand-author source Note text, and never infer, edit, combine, or fabricate
metadata. If no returned metadata record directly supports the cell, leave it
uncited and disclose that gap.

Do not treat presentation as an afterthought. The structure, citations, and format of the deliverable are part of the solution and should influence how information is gathered. Write and format your workbook as you go.

## 4. Communicate and execute the strategy

Before substantial execution, give the user a concise progress update that communicates:

- your understanding of the objective;
- the important workbook or evidence facts you established;
- your intended solution approach;
- how you intend to structure and present the result.

Communicate conclusions and the intended approach and any change in strategy. Keep this brief for simple tasks and more structured for complex tasks.

Then execute the strategy incrementally. Continue reading, researching, calculating, writing, formatting, and validating until the requested deliverable is complete.

If evidence or a tool result invalidates the approach, update the strategy and continue. A failed search or a failed edit is a reason to change the attempt, not a reason to finish.

## 5. Review what remains, then repeat

Review both substance and presentation:

- Does the output answer the user's actual question?
- Is the relevant population covered completely?
- Are calculations, formulas, mappings, rankings, and sourced facts correct?
- Is every externally sourced hardcoded fact, value, identity, or claim grounded in matching evidence with a citation? Are generic presentation headers, labels, and units left uncited unless they themselves contain retrieved information? Are formula cells and blank cells uncited?
- Are assumptions, limitations, and unresolved gaps clearly identified, with the attempts already made?
- Is the structure appropriate for the intended reader?
- Does the workbook follow the Model Formatting guidelines: the active template, organization, or brand font (Arial 8 only as the fallback), consistent row heights and comparable column widths, deliberate wrapping, "N/A" right-aligned, title above the block, concise source under it, and substantive rationale on a dedicated support sheet?
- Are raw-data sheets free of calculations, are substantive narrative notes kept off numerical sheets, and are final presentation numbers linked from supporting sheets rather than duplicated as hardcodes?
- Is the workbook consistently formatted and client-ready?
- Would a strong investment banker be comfortable presenting this work to a senior banker or client?

Keep a coverage record: rows and entities considered, fields completed, what was filtered or deduplicated, what remains unresolved, and which output rows map back to which source rows.

Anything material that this review finds still open is the next problem. Return to section 1 and run the loop again on that remainder. Anything incomplete, unsupported, inconsistent, or not client-ready becomes the focus of the next pass. Reuse evidence that already holds.

Claim completion only when that review finds nothing material left, or a specific fact cannot be found after a different follow-on attempt. The explanation of what was done, and of anything that remains unresolved, must already be with the user before you stop.

# Incremental Change and Review Loop
Work incrementally: inspect the relevant area, make the next bounded change, format it right
review it, and read back the result and iterate till the task is complete. 
Whenever you can, write and format as you go. Landing each change in the workbook as an 
incremental update makes your progress visible, instead of holding the writes and formatting for one final pass.
Again, apply your banker judgement when thinking here. In many cases, the user's ask may require editing or QA on 
a file instead of building something from scratch. In such cases, make sure you preserve existing formulas, links, 
checks, conventions, and intentional hardcodes unless the user asks to change them. Use finance-appropriate labels, units, signs,
precision, chronology, and formula conventions. Never trade completeness for a
quick partial answer.

When a tool fails or a search comes back empty, diagnose the returned evidence and
ask where else a banker would look for that fact. Retry in that place, or with a
materially different question. Do not loop on the same failed action if you can
sense that it will fail again. Again, apply best banker judgement on how you would tackle the problem.
Before stopping, verify calculations, formulae
continuity, requested coverage, formatting consistency, and that the finished
workbook and explanation are client-ready. If a material issue cannot be
resolved, state it precisely and explain why rather than claiming success. Also when part of the work
requires further user help and input, explain which bits and why they need to be looked at rather than claiming failure.

# Modeling skills
You have access to a range of skills for specific modeling tasks which contain more
detailed guidance on those tasks. Use those skills when you can sense that your task needs specialist modeling guidance.
Select the relevant set of skills that you would need to fulfill the deliverable to the expected standard;
the tool expands required dependencies automatically.

# Banker-grade formatting and presentation
Treat formatting as part of the deliverable, not optional decoration. Every workbook you create or modify must be
presentation-ready and meet professional investment-banking standards. Follow the applicable user, organization, and workbook-template style first;
and general formatting and layout skills should be available to you as the house standard. Before completing, review the edited ranges as a banker would: ensure consistent fonts, colours, number formats, formulae formats, units, dates, headers,
spacing, alignment, column widths, input/formula/source conventions, totals, footnotes, and source lines. Preserve the existing workbook’s visual
language when editing an established model. Correct formatting inconsistencies introduced by your work, and do not complete till the output is clear,
consistent, and suitable for review by a senior banker or client.

# Research tool calls
FactSet AI / Finster research tools cannot see or open the current Excel workbook.
They are stateless delegated agents and receive no workbook cells, formulas,
the conversation, prior calls, or hidden reasoning. Never ask them to inspect,
extract, filter, summarize, or cite the open workbook. First use Excel tools to
read the workbook facts needed to frame the question, then include those facts
explicitly in each self-contained research query. Make every research query
self-contained with the company/entity, periods, metrics, units, relevant
workbook facts and assumptions, and exact requested deliverable and format you would like to get the answer.
Never refer only to "this workbook", "the selected cells", or "the context above".
Decompose an overly broad research request into bounded, coherent subqueries by
company, topic, period, source, or analytical question when decomposition is the best strategy.
Ask for the answer in a clear format suited to the deliverable.

Before starting each research stage, identify every bounded research subquery
that can already be fully formulated and independently run from the available context. After choosing
the appropriate query scope, if two or more subqueries are independent,
emit them as separate research tool calls in the same assistant turn. The runtime
can execute up to four read-only calls concurrently. When more than four
independent queries remain, dispatch them in successive groups of up to four.

Do not issue one research call and wait for its result when another known query
can already be written without that result. Different companies, metrics,
periods, topics, or sources should normally be treated as independent unless the
exact output of one call is required to construct another call's arguments.
Sequence calls only when there is a concrete data dependency. After a parallel
group returns, synthesize its results, identify remaining gaps, and then issue
any genuinely dependent follow-up calls.

Every parallel query must be complete and self-contained. Bad: research Company
A, wait, then research Company B. Good: when both questions are already known,
emit the Company A and Company B calls together.

Use research tools only in research-only turns. Never combine a research call
with any workbook, skill, write, or other non-research tool call in the same turn.

# Questions for the user
Call \`ask_user\` alone when two materially different deliverables remain and neither the workbook nor the usual banker standard decides. Give two to four options and mark the one you recommend. The run pauses until the user picks an option or types an answer, then continues with that answer as the tool result. A question you can decide is a decision, not an \`ask_user\` call. Do not call \`complete_run\` to ask a question.

# Completion protocol
You should communicate concise progress while continuing to reason and use tools.
You should expose your reasoning and thinking process to the user. Once your task is complete,
make sure you explain the analysis you did, what is complete, what still needs further input or looking into
from the user and why and the evidence behind that. To stop the loop, you need to call the \`complete_run\` tool
with no arguments and no other tool calls. That will stop the loop. Make sure you have everything in place and have
sent through all the workbook/worksheet changes and explanations to the user before calling this tool.
Again, you should NOT stop your working loop when facing some failures. Read the response, figure
out why and keep trying till your job is done. An incomplete job is not acceptable. Once the task is done, end the
workflow only by calling \`complete_run\`, and call it alone (as the only tool) in its turn.

# Review before finalizing
Before writing the final answer or calling \`complete_run\`, perform
a complete separate review pass over the finished workbook. Make sure you think strategically like a banker
when reviewing and focus on 2 specific things. The content - information you put in the workbook and the
presentation of the workbook. This is a required work stage, not a summary of checks performed earlier.

- Re-read every populated cell in each range or sheet you created or materially
  changed. Verify values and formula text where relevant, and request format
  metadata rather than using a values-only read when reviewing presentation.
- Review the complete edited output, not only calculation anchors, check cells,
  sensitivity tables, or a representative sample. Inspect titles, labels, units,
  assumptions, formulas, outputs, notes, source lines, and the edges and unused
  remainder of any copied or repurposed template block.
- Confirm each worksheet has a clear role. Raw-data sheets must contain no derived
  calculations; calculation sheets must link to their source inputs; final
  presentation sheets must pull numbers from supporting sheets; and long-form
  rationale or methodology must sit on a dedicated support sheet rather than a
  numerical output sheet.
- Check presentation sheet by sheet and if need be cell by cell for the active template: font consistency,
  readable font sizes, font and fill colours, number and date formats, alignment,
  wrapping, capitalization, merged cells, row and column spacing, hierarchy, and residual or
  orphaned formatting. A copied sheet or a contents-only clear retains formatting;
  treat that inherited formatting as unverified until this review confirms it.

## Visual QA contract

This contract applies whenever the run creates or materially changes workbook
presentation. It does not require snapshots for purely numerical work with no
visual decision.

For every materially changed output sheet:

1. Capture a readable snapshot of the complete affected area. Tile large areas
   and take close-up snapshots wherever text or formatting is too small to judge.
2. Inspect the rendered result against the active workbook template, organization
   or brand style, and banker-quality standards. Check hierarchy, fonts, fills,
   visible gridlines, borders, displayed number formats, alignment, wrapping, row
   heights, column widths, spacing, labels, units, totals, footnotes, and source lines.
3. Form a concrete list of every material presentation defect visible in the
   snapshot. A successful write or formatting tool result proves execution only;
   it is not visual proof.
4. Use \`read_range\` with formatting metadata to verify exact values, formulas,
   and number-format codes. Treat live reads as authoritative for numerical and
   format-code correctness; use snapshots to judge rendered presentation.
5. Repair every material defect that can be resolved with the available tools.
6. Capture a fresh snapshot of every repaired area and review it again. Repeat
   the repair-and-review loop while material defects remain.

Do not call \`complete_run\` or describe the workbook as client-ready until the
latest snapshots and live reads show no material presentation defect that can
still be resolved. If visual verification is unavailable or inconclusive,
disclose that limitation instead of claiming the review passed.

- Do not stop until your review passes the quality bar in terms of content and formatting
  or you have genuinely exhausted all your options. If it is the latter, explain very clearly
  how you have tried everything and what is still missing and why it failed your review.

Only proceed to the final answer and \`complete_run\` AFTER this separate review
finds no material correctness, coverage, citation, or presentation issue left
that can be resolved with the available tools. Make sure you have mentioned your
review strategy, observations, done all follow up corrections and finalized your
review notes and observations to the user.
`.trim();
  const mandatoryHygieneGuidance = buildExecutorSkillAssembly(undefined, config.brandId).skillBlock;
  const researchGuidance = getFinsterQueryToolInstruction(
    undefined,
    config.toolCallContext?.researchContext,
    { includeFilingsQuery: false, includeStructuredCitations: true }
  );
  const selectedFileCount = new Set(
    (
      config.toolCallContext?.researchContext?.manualUserFileIds ??
      config.toolCallContext?.researchContext?.userFileIds ??
      []
    )
      .map((fileId) => fileId.trim())
      .filter(Boolean)
  ).size;
  const selectedFileGuidance =
    selectedFileCount > 0
      ? `
# Selected-file evidence
The user explicitly selected ${selectedFileCount} ${selectedFileCount === 1 ? "file" : "files"} for this request. Treat the selected files as required evidence in your assessment of what to do.

First inspect enough workbook context to frame a self-contained research query,
then use the full FactSet AI / Finster research tool with the immutable selected-file
scope. Incorporate the retrieved file evidence into your reasoning and any
workbook changes. If retrieval fails or the files contain no relevant evidence,
say so explicitly and continue only with work that does not depend on them.
Do not claim that you considered the selected files unless retrieval succeeded.
`.trim()
      : "";

  return withChatHistoryGuidance(
    [bankerOperatingStandard, mandatoryHygieneGuidance, selectedFileGuidance, researchGuidance]
      .filter(Boolean)
      .join("\n\n")
  );
}

/** Fully assembled state and tool surface for one native ReAct run. */
export interface NativeReactRuntime {
  /** Internal synthetic plan state used by existing write safety checks. */
  planState: ExecutorPlanState;
  /** Shared mutation/result accumulators consumed by finalization. */
  accumulators: ExecutorRunAccumulators;
  /** Workbook target and run-scoped safety state. */
  session: ExecutorRunSessionState;
  /** Runtime context retained for diagnostics and focused tests. */
  context: ExecutorToolRuntimeContext;
  /** Provider-visible native tool definitions. */
  toolDefs: readonly ChartCapabilityToolDef[];
  /** Native dispatch map with plan-control handlers removed. */
  executors: Record<string, RuntimeToolExecutor>;
  /** Research source cards accumulated without replaying citations to the model. */
  retrievedSources: ExcelAgentSource[];
  /** Raw research citations accumulated for UI and persistence parity. */
  retrievedCitations: unknown[];
}

/** Non-model-facing metadata retained until one tool completion event is emitted. */
interface NativeToolCompletionMetadata {
  elapsedMs: number;
  researchCitations?: unknown[];
  researchCitedValuesByUuid?: Record<string, string>;
  observationMetadata?: Record<string, unknown>;
  /** Task-pane-only metadata excluded from model replay and Langfuse. */
  displayMetadata?: Record<string, unknown>;
}

/** Injectable seams for focused native-runner tests. */
export interface NativeReactRunnerDependencies {
  /** Bypasses provider transport while retaining the real harness/runtime. */
  requestTurn?: NativeReactHarnessConfig["requestTurn"];
  /** Optional provider adapter override. */
  adapter?: ExecutorProviderAdapter;
  /** Optional resolved model override. */
  model?: ExecutorModelConfig;
  /** Optional workbook exporter used by OpenAI input-file requests. */
  exportWorkbookAsBase64?: typeof getWorksheetsAsBase64;
  /** Optional raw workbook byte ceiling for focused limit tests. */
  maxWorkbookInputFileBytes?: number;
  /** Optional hard model-turn cap. */
  maxIterations?: number;
  /** Optional hard wall-clock budget. */
  maxDurationMs?: number;
  /** Optional unchanged-repeat budget. */
  maxStagnantIterations?: number;
  /** Optional clock for deterministic timeout tests. */
  now?: () => number;
}

/** Request-scoped tool authorization resolved before native runtime assembly. */
export interface NativeToolRouting {
  /** Expose the direct single-entity ID lookup tool. */
  includeIdLookup: boolean;
  /** Authorized identity fields inferred from the user request. */
  idLookupRoutingIntent: IdLookupRoutingIntent;
}

/**
 * Build the native runtime.
 *
 * @param config - Existing runner dependencies and run-scoped workbook target.
 * @param routing - Optional request-scoped direct/workload tool authorization.
 * @returns Native-only definitions, dispatchers, and synthetic safety state.
 */
export function createNativeReactRuntime(
  config: ExecutorRunnerConfig,
  _routing?: NativeToolRouting
): NativeReactRuntime {
  const planState: ExecutorPlanState = {
    summary: "Native ReAct run",
    estimatedComplexity: "moderate",
    currentStepId: NATIVE_RUN_STEP_ID,
    steps: [
      {
        id: NATIVE_RUN_STEP_ID,
        status: "running",
        planStep: {
          step: 1,
          description: config.userRequest,
          action: "native_react",
          ...(config.sessionLockSheet ? { targetSheet: config.sessionLockSheet } : {}),
        },
      },
    ],
  };
  const accumulators = createNativeAccumulators(config.userRequest);
  const session: ExecutorRunSessionState = {
    sessionLockSheet: config.sessionLockSheet,
    userRequest: config.userRequest,
    pinnedWorkbookName: config.pinnedWorkbookName,
    citationNoteCapability:
      config.bridgeAvailable === false ? "unsupported" : config.citationNoteCapability,
    citationNoteAttachmentDisabled:
      config.bridgeAvailable === false || config.citationNoteCapability === "unsupported",
  };
  const context: ExecutorToolRuntimeContext = {
    planState,
    onPlanStateChange: config.onPlanStateChange,
    applyWorkbookModifications: config.applyWorkbookModifications,
    accumulators,
    session,
    onWorkbookHandoff: config.onWorkbookHandoff,
    beginWorkbookHandoff: config.beginWorkbookHandoff,
    researchContext: config.toolCallContext?.researchContext,
    userSkillGuidance: config.toolCallContext?.userSkillGuidance,
    langfuseContext: config.langfuseContext,
    signal: config.signal,
    getRemainingExecutorIterations: () => MAX_EXECUTOR_ITERATIONS,
    factSetFormatRegistry: new FactSetFormulaFormatRegistry(),
    refreshFactSetData: config.refreshFactSetData,
    recalculateWorkbook: config.recalculateWorkbook,
  };
  const shared = createExecutorToolRuntime(context);
  const toolDefs: ChartCapabilityToolDef[] = [
    ...shared.toolDefs.map((tool) =>
      tool.name === NATIVE_SET_RANGE_VALUES_TOOL.name ? NATIVE_SET_RANGE_VALUES_TOOL : tool
    ),
    SET_CITATIONS_TOOL,
    COMPLETE_RUN_TOOL,
    ASK_USER_TOOL,
  ];
  const executors = shared.executors;

  return {
    planState,
    accumulators,
    session,
    context,
    toolDefs,
    executors,
    retrievedSources: [],
    retrievedCitations: [],
  };
}

/**
 * Authorize the direct ID-lookup tool for eligible single-company requests.
 * Legacy worksheet workload tools remain disconnected from native ReAct.
 *
 * @param config - Run configuration, user request, workbook metadata, and gates.
 * @returns The authorized direct lookup selection.
 */
async function resolveNativeToolRouting(config: ExecutorRunnerConfig): Promise<NativeToolRouting> {
  const detectedFromText = detectIdLookupRoutingIntent(config.userRequest, config.userRequest);
  const companyList = config.workbookSnapshot
    ? detectWorksheetCompanyList(config.workbookSnapshot)
    : detectWorksheetCompanyListFromContext(config.workbookContext);
  const worksheetRoute = await resolveWorksheetColumnRoute({
    userRequest: config.userRequest,
    tabularEnabled: config.excelTabularQuery?.enabled === true,
    companyListPresent: companyList.present,
    idLookupIntent: detectedFromText,
    signal: config.signal,
  });
  const detected = worksheetRoute.idLookupIntent;
  const isWorksheetListLookup =
    detected.active &&
    (detectIdLookupWorksheetWorkload(config.userRequest) ||
      worksheetRoute.inferredWorksheetList === true);
  const sourceRestricted = isDirectIdLookupSourceRestricted(
    config.toolCallContext?.researchContext
  );
  const directEligible = detected.active && !isWorksheetListLookup && !sourceRestricted;
  const availability = directEligible
    ? await resolveIdLookupAvailability({
        log: (message) => config.onEvent?.({ type: "status", message }),
      })
    : { available: false as const };
  const includeIdLookup = directEligible && availability.available;
  return {
    includeIdLookup,
    idLookupRoutingIntent: includeIdLookup ? detected : { active: false, requestedFields: [] },
  };
}

/**
 * Build the native user envelope with metadata-only workbook bootstrap.
 *
 * @param config - Existing run configuration and workbook snapshot.
 * @returns Serialized user request safe for the first native model turn.
 */
export function buildNativeReactUserMessage(config: ExecutorRunnerConfig): string {
  return JSON.stringify({
    userRequest: config.userRequest,
    ...(config.chatHistory?.trim() ? { chatHistory: config.chatHistory.trim() } : {}),
    internalStepId: NATIVE_RUN_STEP_ID,
    workbook: JSON.parse(
      serializeNativeWorkbookBootstrap({
        workbookSnapshot: config.workbookSnapshot,
        workbookContext: config.workbookContext,
      })
    ) as unknown,
    responseFormat:
      "Use tools until work is complete, write the final user-facing explanation as assistant text, then call complete_run with no arguments and no other tool calls.",
  });
}

/**
 * Execute one native ReAct run.
 *
 * @param config - Existing executor host, context, and observability callbacks.
 * @param dependencies - Optional provider and budget seams for focused tests.
 * @returns Existing unified result shape consumed by Excel finalization.
 */
export async function runNativeReactExecutor(
  config: ExecutorRunnerConfig,
  dependencies: NativeReactRunnerDependencies = {}
): Promise<ExecutorUnifiedRunResult> {
  const routing = await resolveNativeToolRouting(config);
  const runtime = createNativeReactRuntime(config, routing);
  const model = dependencies.model ?? resolveEffectiveExcelExecutorModel();
  const adapter = dependencies.adapter ?? getExecutorAdapter(model.provider);
  const tools = adapter.serializeTools({
    scope: "react-executor",
    extraTools: runtime.toolDefs,
    options: {
      includeIdLookup: routing.includeIdLookup,
      includeFilingsQuery: false,
    },
  });
  const instructions = appendSavedContextRule(
    buildNativeReactSystemPrompt(config, routing),
    config.savedContext
  );
  const initialTurns: AgentTurn[] = [
    ...(prependSavedContext(config.preludeUserMessages, config.savedContext) ?? []).map(
      (content): AgentTurn => ({ kind: "user", content })
    ),
    { kind: "user", content: buildNativeReactUserMessage(config) },
  ];
  const requestTurn =
    dependencies.requestTurn ??
    createProviderTurnRequester(
      config,
      adapter,
      model,
      tools,
      instructions,
      () => runtime.session.pinnedWorkbookName,
      dependencies.exportWorkbookAsBase64 ?? getWorksheetsAsBase64,
      dependencies.maxWorkbookInputFileBytes ?? MAX_EXCEL_WORKBOOK_INPUT_FILE_BYTES
    );
  const completionMetadataByCallId = new Map<string, NativeToolCompletionMetadata>();
  const executedWriteFingerprints = new Set<string>();
  const granularRecoveryKeys = new Set<string>();
  let blockedOperation: ExecutorBlockedOperation | undefined;
  const templateOutcomes: ApplyTemplateOutcome[] = [];
  const nativeToolCallContext = withApplyTemplateOutcomeCapture(
    config.toolCallContext,
    templateOutcomes
  );

  const harnessResult = await runBoundedNativeReactHarness({
    initialTurns,
    requestTurn,
    executeTool: async (call, execution) => {
      const args = parseToolArguments(call.arguments);
      const startedAtMs = Date.now();
      const templateOutcomeStart = templateOutcomes.length;
      let result: ToolCallDetailedResult;
      const writeFingerprint = isReplayGuardedWriteToolCall(call.name, args)
        ? stableWriteFingerprint(call.name, args)
        : undefined;
      const recoveryKey = granularRecoveryKey(call.name, args);
      const remediationCells =
        blockedOperation && isReplayGuardedWriteToolCall(call.name, args)
          ? narrowGranularRemediationCells(blockedOperation, call.name, args)
          : undefined;
      if (blockedOperation && blockedOperationExactlyMatches(blockedOperation, call.name, args)) {
        result = {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            code: "partial_write_replay_blocked",
            editedCells: blockedOperation.confirmedEditedCells,
            error: "This exact write may have partially applied and was not replayed.",
            instruction:
              "Read back the original targets, then issue a narrower exact-shaped write only for coordinates still missing.",
          }),
        };
      } else if (
        blockedOperation &&
        !remediationCells &&
        isReplayGuardedWriteToolCall(call.name, args) &&
        writeOverlapsBlockedOperation(blockedOperation, call.name, args)
      ) {
        result = {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            code: "blocked_write_requires_resolution",
            error: "This write overlaps cells whose prior host acknowledgement is unresolved.",
            instruction:
              "Resolve the blocked target by exact read-back and narrower exact-shaped writes before writing these cells again.",
          }),
        };
      } else if (recoveryKey && granularRecoveryKeys.has(recoveryKey)) {
        result = {
          output: JSON.stringify({
            ok: false,
            applied: false,
            recoveryExhausted: true,
            retryable: false,
            code: "range_write_recovery_exhausted",
            error:
              "The one readback-first recovery round for this exact range write is already exhausted.",
            instruction:
              "Do not replay the original write. Read the target and issue only narrower exact-shaped writes for proven missing coordinates.",
          }),
        };
      } else if (writeFingerprint && executedWriteFingerprints.has(writeFingerprint)) {
        result = {
          output: JSON.stringify({
            ok: true,
            applied: true,
            replayPrevented: true,
            writeFingerprint,
            message: "This exact workbook write was already confirmed and was not replayed.",
          }),
        };
      } else {
        try {
          const runtimeExecutor = runtime.executors[call.name];
          if (runtimeExecutor) {
            const runtimeResult = await runtimeExecutor(args, execution.signal, {
              callId: call.callId,
              iteration: execution.iteration,
            });
            result = typeof runtimeResult === "string" ? { output: runtimeResult } : runtimeResult;
          } else {
            const toolCallContext: ToolCallContext = {
              ...nativeToolCallContext,
              ...(isAnyResearchToolName(call.name)
                ? {
                    researchExecutionMode: "stream" as const,
                    onResearchSubEvent: (event: FinsterStreamSubEvent) => {
                      try {
                        config.onEvent?.({
                          type: "research_sub_event",
                          callId: call.callId,
                          event,
                        });
                      } catch {
                        // Display-only streaming must never change research execution.
                      }
                    },
                  }
                : {}),
              ...(call.name === ID_LOOKUP_TOOL_NAME
                ? {
                    onIdLookupProgress: (progress: IdLookupBatchProgress) => {
                      try {
                        config.onEvent?.({
                          type: "workload_progress",
                          callId: call.callId,
                          name: call.name,
                          iteration: execution.iteration,
                          kind: "requests",
                          ...progress,
                        });
                      } catch {
                        // Display-only progress must never change tool execution.
                      }
                    },
                  }
                : {}),
            };
            result = await executeToolCallDetailed(
              call.name,
              args,
              execution.signal,
              toolCallContext
            );
          }
        } catch (error) {
          if (
            !writeFingerprint ||
            error instanceof ExecutorWorkbookHandoffError ||
            execution.signal?.aborted
          ) {
            throw error;
          }
          result = {
            output: JSON.stringify({
              ok: false,
              applied: false,
              ambiguousWrite: true,
              failedActions: ["tool dispatch acknowledgement"],
              error: error instanceof Error ? error.message : String(error),
            }),
          };
        }
        result = await recoverAmbiguousGranularRangeWrite({
          toolName: call.name,
          parsedArgs: args,
          result,
          executors: runtime.executors,
          signal: execution.signal,
          pinnedWorkbookName: runtime.session.pinnedWorkbookName,
          recoveryKeys: granularRecoveryKeys,
          accumulators: runtime.accumulators,
          onEvent: config.onEvent,
        });
        const parsedOutput = safeParseToolArguments(result.output);
        if (
          blockedOperation &&
          annotationReadResolvesBlockedOperation(
            blockedOperation,
            call.name,
            args,
            parsedOutput,
          )
        ) {
          blockedOperation = undefined;
        }
        if (
          writeFingerprint &&
          parsedOutput &&
          typeof parsedOutput === "object" &&
          !Array.isArray(parsedOutput) &&
          isConfirmedWriteResult(parsedOutput as Record<string, unknown>)
        ) {
          executedWriteFingerprints.add(writeFingerprint);
          if (remediationCells && blockedOperation) {
            const remediated = new Set(
              remediationCells.map((address) => address.trim().toLowerCase())
            );
            const remaining = (blockedOperation.unresolvedCells ?? []).filter(
              (address) => !remediated.has(address.trim().toLowerCase())
            );
            blockedOperation =
              remaining.length > 0
                ? {
                    ...blockedOperation,
                    unresolvedCells: remaining,
                  }
                : undefined;
          }
        } else if (
          writeFingerprint &&
          parsedOutput &&
          typeof parsedOutput === "object" &&
          !Array.isArray(parsedOutput) &&
          shouldCheckpointBlockedWrite(parsedOutput as Record<string, unknown>)
        ) {
          const parsedRecord = parsedOutput as Record<string, unknown>;
          const unresolvedCells = Array.isArray(parsedRecord.recoveryAttempts)
            ? parsedRecord.recoveryAttempts.flatMap((attempt) => {
                if (!attempt || typeof attempt !== "object" || Array.isArray(attempt)) {
                  return [];
                }
                const candidate = attempt as {
                  address?: unknown;
                  applied?: unknown;
                };
                return candidate.applied !== true && typeof candidate.address === "string"
                  ? [candidate.address]
                  : [];
              })
            : [];
          blockedOperation = {
            toolName: call.name,
            args,
            reason:
              typeof parsedRecord.error === "string"
                ? parsedRecord.error
                : "Workbook write acknowledgement is unresolved.",
            confirmedEditedCells: Array.isArray(parsedRecord.editedCells)
              ? parsedRecord.editedCells.filter(
                  (value): value is string => typeof value === "string"
                )
              : undefined,
            readbackObserved: unresolvedCells.length > 0 ? true : undefined,
            unresolvedCells: unresolvedCells.length > 0 ? unresolvedCells : undefined,
          };
        }
      }
      const newTemplateOutcomes = templateOutcomes.slice(templateOutcomeStart);
      if (
        call.name === "apply_template" &&
        newTemplateOutcomes.some((outcome) => outcome.success)
      ) {
        runtime.accumulators.workbookEditAttempted = true;
        const processed = await processApplyTemplateOutcomes(
          newTemplateOutcomes,
          runtime.session,
          {
            probeActiveContext: config.probeActiveContext,
            bridgeAvailable: config.bridgeAvailable !== false,
            onWorkbookHandoff: config.onWorkbookHandoff,
          },
          config.onEvent
        );
        if (processed.shouldStop) {
          throw new ExecutorWorkbookHandoffError(
            "apply_template",
            runtime.session.pinnedWorkbookName ?? "template workbook",
            new Error(processed.reason)
          );
        }
      }
      if (call.name === "generate_formula") {
        runtime.context.factSetFormatRegistry?.recordGenerateFormulaOutput(
          safeParseToolArguments(result.output)
        );
      }
      result = exposeNativeResearchCitations(call.name, result);
      const completionMetadata = buildNativeToolCompletionMetadata({
        toolName: call.name,
        result,
        elapsedMs: Math.max(0, Date.now() - startedAtMs),
      });
      completionMetadataByCallId.set(call.callId, completionMetadata);
      if (isAnyResearchToolName(call.name) && inferToolSuccess(result.output)) {
        ingestResearchToolResult(
          result.output,
          runtime.retrievedSources,
          runtime.retrievedCitations,
          runtime.accumulators,
          result.researchCitations,
          { persistCitationIndex: false }
        );
      }
      postExcelToolObservation({
        context: config.langfuseContext,
        phase: "excel_unified_agent",
        executorMode: "native-react",
        toolName: call.name,
        args,
        result,
        startedAtMs,
      });
      return result;
    },
    isStatefulToolCall: (call) =>
      isStatefulToolCall(call.name, safeParseToolArguments(call.arguments)),
    isResearchToolCall: (call) => isAnyResearchToolName(call.name),
    progressSignature: () =>
      [
        runtime.accumulators.editedCells.length,
        runtime.accumulators.stepResults.length,
        runtime.accumulators.workbookEditAttempted,
      ].join("|"),
    maxIterations: dependencies.maxIterations,
    maxDurationMs: dependencies.maxDurationMs,
    maxStagnantIterations: dependencies.maxStagnantIterations,
    now: dependencies.now,
    signal: config.signal,
    isTerminalToolError: (error) => error instanceof ExecutorWorkbookHandoffError,
    answerUserQuestion: config.answerUserQuestion,
    onEvent: (event) => {
      forwardHarnessEvent(event, config.onEvent, completionMetadataByCallId);
    },
  });

  const attemptedEdit =
    runtime.accumulators.workbookEditAttempted || runtime.accumulators.editedCells.length > 0;
  if (attemptedEdit && !config.signal?.aborted) {
    await executeFactSetFormatSafetyPass(runtime.context);
  }
  if (!attemptedEdit) {
    return {
      mode: harnessResult.status === "completed" ? "answer" : "failed",
      reply: harnessResult.reply,
      workbookEditAttempted: false,
      retrievedSources: dedupeNativeSources(runtime.retrievedSources),
      retrievedCitations: runtime.retrievedCitations,
      retrievedCitedValuesByUuid: runtime.accumulators.retrievedCitedValuesByUuid ?? {},
    };
  }

  return buildNativeWorkbookResult(runtime, harnessResult);
}

/**
 * Initialize the mutable result collectors expected by the hardened shared
 * executor-tool runtime.
 *
 * Native mode owns fresh collectors per run to prevent workbook edits,
 * citations, sizing hints, or refresh debt from leaking across conversations.
 *
 * @param userRequest - Original user request shown in run diagnostics.
 * @returns Empty run-scoped accumulators compatible with shared tool handlers.
 */
function createNativeAccumulators(userRequest: string): ExecutorRunAccumulators {
  return {
    stepResults: [],
    stepResultIndexById: new Map(),
    stepDisplayResults: [userRequest],
    stepStatuses: ["queued"],
    lastModifications: null,
    editedCells: [],
    formulaWriteTargets: [],
    fdsFormulaWriteTargets: [],
    fdsFormulaWriteAttempts: {},
    unavailableFdsFormulaTargets: [],
    fdsDateAxisWriteTargets: [],
    autofitSheetNames: new Set(),
    autofitTargets: [],
    columnSizingHints: [],
    columnSizingCellEvidence: {},
    explicitColumnWidthKeys: [],
    fdsRefreshSheets: new Set(),
    deferredRefreshAllRequested: false,
    deferredRecalcMode: null,
    workbookEditAttempted: false,
    sourceNoteAudits: [],
    retrievedCitedValuesByUuid: {},
  };
}

/**
 * Adapt provider-neutral harness turns into one provider request callback.
 *
 * The returned callback preserves existing model selection, credentials,
 * retries, streaming reasoning, and Langfuse generation metadata while asking
 * only native mode for a plain-text terminal response.
 *
 * @param config - Existing executor transport and observability dependencies.
 * @param adapter - Provider implementation selected for this run.
 * @param model - Immutable model configuration selected at run start.
 * @param tools - Provider-serialized native tool surface.
 * @param instructions - Complete native system prompt for each model turn.
 * @param getPinnedWorkbookName - Reads the live pin after any workbook handoff.
 * @param exportWorkbookAsBase64 - Exports one current workbook snapshot with a long host timeout.
 * @param maxWorkbookInputFileBytes - Raw byte ceiling before base64 expansion.
 * @returns A harness-compatible function that performs one model turn.
 */
function createProviderTurnRequester(
  config: ExecutorRunnerConfig,
  adapter: ExecutorProviderAdapter,
  model: ExecutorModelConfig,
  tools: readonly unknown[],
  instructions: string,
  getPinnedWorkbookName: () => string | undefined,
  exportWorkbookAsBase64: typeof getWorksheetsAsBase64,
  maxWorkbookInputFileBytes: number
): NativeReactHarnessConfig["requestTurn"] {
  return async ({ turns, iteration, signal }): Promise<AgentTurnResult> => {
    const encoded = adapter.encodeTurns(turns);
    const projectedItems = adapter.projectItems(encoded);
    let workbookInputFileItem: ReturnType<typeof buildExcelWorkbookInputFileItem> | null = null;
    let workbookInputFileBytes: number | null = null;

    if (model.provider === "openai" && iteration === 1) {
      const pinnedWorkbookName = getPinnedWorkbookName();
      try {
        const base64 = await exportWorkbookAsBase64(
          pinnedWorkbookName ? { workbookName: pinnedWorkbookName } : {},
          LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS
        );
        workbookInputFileItem = buildExcelWorkbookInputFileItem(
          base64,
          pinnedWorkbookName,
          maxWorkbookInputFileBytes
        );
        workbookInputFileBytes = decodeWorkbookBase64ByteLength(base64);
      } catch (error) {
        console.warn(
          "[Excel] Current workbook input_file unavailable; continuing with live read tools:",
          error
        );
        postExcelLangfuseObservation(
          config.langfuseContext,
          {
            name: "excel.native.workbook_input_file",
            metadata: {
              outcome: "export_unavailable",
              iteration,
              errorType:
                error instanceof Error && error.name ? error.name : typeof error,
            },
            level: "ERROR",
            endTime: new Date().toISOString(),
          },
          { phase: "excel_unified_agent" },
        );
      }
    }

    const buildProviderRequest = (includeWorkbook: boolean) =>
      adapter.buildRequest({
        model,
        instructions,
        items:
          includeWorkbook && workbookInputFileItem
            ? [...projectedItems, workbookInputFileItem]
            : projectedItems,
        tools,
        forcedToolName: null,
        terminalTextFormat: "plain_text",
      });

    let streamedReasoning = "";
    let streamedAssistantText = "";
    const streamProviderRequest = (request: ReturnType<ExecutorProviderAdapter["buildRequest"]>) =>
      adapter.streamTurn({
        request: () =>
          fetchWithRetry(
            request.url,
            config.withSignal(
              withOptionalExcelLangfuseMetadata(
                withCredentials({
                  method: "POST",
                  body: JSON.stringify(request.body),
                  ...(request.headers ? { headers: request.headers } : {}),
                }),
                config.langfuseContext,
                buildExcelUnifiedAgentGenerationMetadata({
                  executorMode: "native-react",
                  provider: model.provider,
                  model: model.model,
                  iteration,
                  stream: true,
                  toolCount: tools.length,
                })
              )
            ),
            2,
            "native-react-executor"
          ),
        contextLabel: `native-react-executor ${model.label}`,
        signal,
        maxBodyRetries: 1,
        onText: (partial) => {
          const delta = partial.startsWith(streamedAssistantText)
            ? partial.slice(streamedAssistantText.length)
            : partial;
          streamedAssistantText = partial;
          if (!delta) return;
          config.onEvent?.({
            type: "assistant_text_delta",
            iteration,
            text: delta,
          });
        },
        onReasoning: (partial) => {
          const delta = partial.startsWith(streamedReasoning)
            ? partial.slice(streamedReasoning.length)
            : partial;
          streamedReasoning = partial;
          if (!delta) return;
          config.onReasoningDelta?.(delta, iteration);
          config.onEvent?.({
            type: "reasoning_delta",
            iteration,
            text: delta,
          });
        },
        onRetry: () => {
          streamedAssistantText = "";
          config.onEvent?.({
            type: "assistant_text_reset",
            iteration,
          });
          config.onEvent?.({
            type: "status",
            message: `Model stream interrupted; retrying native iteration ${iteration}.`,
          });
        },
      });

    try {
      const result = await streamProviderRequest(buildProviderRequest(true));
      if (workbookInputFileItem && workbookInputFileBytes !== null) {
        const filename = workbookInputFileItem.content[0].filename;
        console.info("[Excel] Current workbook input_file accepted.", {
          iteration,
          filename,
          bytes: workbookInputFileBytes,
        });
        config.onEvent?.({
          type: "status",
          message: `📎 Workbook input_file accepted for iteration ${iteration}: ${filename} (${workbookInputFileBytes} bytes).`,
        });
        postExcelLangfuseObservation(
          config.langfuseContext,
          {
            name: "excel.native.workbook_input_file",
            metadata: {
              outcome: "accepted",
              iteration,
              filename,
              bytes: workbookInputFileBytes,
            },
            endTime: new Date().toISOString(),
          },
          { phase: "excel_unified_agent" },
        );
      }
      return result;
    } catch (error) {
      if (!workbookInputFileItem || !isExcelWorkbookInputFileError(error)) {
        throw error;
      }

      console.warn(
        "[Excel] OpenAI rejected the current workbook input_file; retrying this turn with live read tools:",
        error
      );
      postExcelLangfuseObservation(
        config.langfuseContext,
        {
          name: "excel.native.workbook_input_file",
          metadata: {
            outcome: "provider_rejected",
            iteration,
            code:
              error && typeof error === "object" && "code" in error
                ? String(error.code)
                : undefined,
          },
          level: "ERROR",
          endTime: new Date().toISOString(),
        },
        { phase: "excel_unified_agent" },
      );
      streamedAssistantText = "";
      streamedReasoning = "";
      return streamProviderRequest(buildProviderRequest(false));
    }
  };
}

/**
 * Translate a native mutation run into the existing workbook-result contract.
 *
 * @param runtime - Run-scoped tools, session, and mutation accumulators.
 * @param harnessResult - Terminal bounded-loop result.
 * @returns Existing Excel workbook result consumed by taskpane finalization.
 */
function buildNativeWorkbookResult(
  runtime: NativeReactRuntime,
  harnessResult: Awaited<ReturnType<typeof runBoundedNativeReactHarness>>
): ExecutorRunResult {
  return {
    mode:
      harnessResult.status === "completed"
        ? "workbook_edit"
        : harnessResult.status === "failed"
          ? "failed"
          : "partial",
    reply: harnessResult.reply,
    retrievedSources: dedupeNativeSources(runtime.retrievedSources),
    retrievedCitations: runtime.retrievedCitations,
    retrievedCitedValuesByUuid: runtime.accumulators.retrievedCitedValuesByUuid ?? {},
    plan: {
      summary: "",
      estimatedComplexity: "moderate",
      steps: [],
    },
    stepResults: [],
    stepDisplayResults: [],
    stepStatuses: [],
    lastModifications: runtime.accumulators.lastModifications,
    editedCells: runtime.accumulators.editedCells,
    formulaWriteTargets: runtime.accumulators.formulaWriteTargets,
    autofitSheetNames: runtime.accumulators.autofitSheetNames,
    autofitTargets: runtime.accumulators.autofitTargets,
    columnSizingHints: runtime.accumulators.columnSizingHints,
    explicitColumnWidthKeys: runtime.accumulators.explicitColumnWidthKeys,
    fdsRefreshSheets: runtime.accumulators.fdsRefreshSheets,
    deferredRefreshAllRequested: runtime.accumulators.deferredRefreshAllRequested,
    deferredRecalcMode: runtime.accumulators.deferredRecalcMode,
    sessionLockSheet: runtime.session.sessionLockSheet,
    workbookEditAttempted: true,
    finalPinnedWorkbookName: runtime.session.pinnedWorkbookName,
  };
}

/**
 * Parse model-emitted tool arguments for dispatch.
 *
 * @param value - JSON text emitted by the provider for one tool call.
 * @returns Parsed JSON value without imposing a tool-specific schema.
 * @throws An error when the provider emits malformed JSON.
 */
function parseToolArguments(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    throw new Error("The model emitted invalid JSON tool arguments.");
  }
}

/**
 * Parse tool arguments for diagnostics without affecting execution.
 *
 * @param value - Potential JSON argument text.
 * @returns Parsed value, or `null` when parsing fails.
 */
function safeParseToolArguments(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

/**
 * Expose compact resolved citation metadata only to native Excel research.
 *
 * Raw provider envelopes remain on the non-model-facing side channel used by
 * the taskpane. The model receives only records referenced by the final answer.
 */
function exposeNativeResearchCitations(
  toolName: string,
  result: ToolCallDetailedResult
): ToolCallDetailedResult {
  if (!isResearchToolName(toolName) || !inferToolSuccess(result.output)) {
    return result;
  }
  const parsed = parseObjectOutput(result.output);
  const answer = typeof parsed.answer === "string" ? parsed.answer : "";
  const rawCitations = Array.isArray(result.researchCitations)
    ? result.researchCitations
    : Array.isArray(parsed.citations)
      ? parsed.citations
      : [];
  return {
    ...result,
    ...(rawCitations.length > 0 ? { researchCitations: rawCitations } : {}),
    output: JSON.stringify({
      answer,
      citations: normalizeResearchCitationsForModel(answer, rawCitations),
    }),
  };
}

/**
 * Build the non-model-facing metadata needed by the established taskpane tool
 * completion contract.
 *
 * Provider-side citations take precedence over legacy citations embedded in
 * the model-facing JSON. Research evidence is only attached for a successful
 * research call; observation metadata remains available for every tool, while
 * display metadata is forwarded only to the task-pane lifecycle event.
 *
 * @param input - Completed native tool identity, detailed result, and duration.
 * @returns Metadata retained until the harness emits the matching completion.
 */
function buildNativeToolCompletionMetadata(input: {
  toolName: string;
  result: ToolCallDetailedResult;
  elapsedMs: number;
}): NativeToolCompletionMetadata {
  const isSuccessfulResearch =
    isAnyResearchToolName(input.toolName) && inferToolSuccess(input.result.output);
  const researchCitations = isSuccessfulResearch ? resolveResearchCitations(input.result) : [];
  const researchCitedValuesByUuid =
    researchCitations.length > 0
      ? extractCitedValuesById({
          ...parseObjectOutput(input.result.output),
          citations: researchCitations,
        })
      : {};

  return {
    elapsedMs: input.elapsedMs,
    ...(researchCitations.length > 0 ? { researchCitations } : {}),
    ...(Object.keys(researchCitedValuesByUuid).length > 0 ? { researchCitedValuesByUuid } : {}),
    ...(input.result.observationMetadata
      ? { observationMetadata: input.result.observationMetadata }
      : {}),
    ...(input.result.displayMetadata
      ? { displayMetadata: input.result.displayMetadata }
      : {}),
  };
}

/**
 * Resolve citations using the same side-channel-first compatibility policy as
 * the native executor.
 *
 * @param result - Detailed research tool result.
 * @returns Raw citations safe for UI/persistence handling, never model replay.
 */
function resolveResearchCitations(result: ToolCallDetailedResult): unknown[] {
  if (Array.isArray(result.researchCitations)) {
    return result.researchCitations;
  }
  const parsed = parseObjectOutput(result.output);
  return Array.isArray(parsed.citations) ? parsed.citations : [];
}

/**
 * Parse a tool output as an object without allowing malformed or array-shaped
 * payloads to affect native finalization.
 *
 * @param output - Model-facing tool output.
 * @returns Parsed object, or an empty object when unavailable.
 */
function parseObjectOutput(output: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(output) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * De-duplicate source cards while preserving first-seen research order.
 *
 * @param sources - Source cards accumulated from successful research calls.
 * @returns Stable de-duplicated source cards.
 */
function dedupeNativeSources(sources: readonly ExcelAgentSource[]): ExcelAgentSource[] {
  const seen = new Set<string>();
  return sources.filter((source) => {
    const key = [source.id, source.url ?? "", source.title, source.source].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Map native harness lifecycle events onto the established taskpane event
 * contract.
 *
 * @param event - Provider-neutral harness event.
 * @param onEvent - Optional existing executor event sink.
 * @param completionMetadataByCallId - Detailed evidence held outside model
 *   replay until the matching completion event is emitted.
 * @returns Nothing.
 */
function forwardHarnessEvent(
  event: Parameters<NonNullable<NativeReactHarnessConfig["onEvent"]>>[0],
  onEvent?: (event: ExecutorRunEvent) => void,
  completionMetadataByCallId: Map<string, NativeToolCompletionMetadata> = new Map()
): void {
  if (!onEvent) return;
  switch (event.type) {
    case "iteration_start":
      onEvent(event);
      return;
    case "tool_call_started":
      onEvent({
        type: "tool_call_started",
        name: event.call.name,
        args: safeParseToolArguments(event.call.arguments),
        iteration: event.iteration,
        callId: event.call.callId,
      });
      return;
    case "tool_call_completed": {
      const metadata = completionMetadataByCallId.get(event.call.callId);
      completionMetadataByCallId.delete(event.call.callId);
      onEvent({
        type: "tool_call_completed",
        name: event.call.name,
        result: event.output,
        resultPreview: event.output.slice(0, 500),
        elapsedMs: metadata?.elapsedMs ?? 0,
        callId: event.call.callId,
        status: inferToolSuccess(event.output) ? "ok" : "error",
        iteration: event.iteration,
        ...(metadata?.researchCitations ? { researchCitations: metadata.researchCitations } : {}),
        ...(metadata?.researchCitedValuesByUuid
          ? {
              researchCitedValuesByUuid: metadata.researchCitedValuesByUuid,
            }
          : {}),
        ...(metadata?.observationMetadata
          ? { observationMetadata: metadata.observationMetadata }
          : {}),
        ...(metadata?.displayMetadata
          ? { displayMetadata: metadata.displayMetadata }
          : {}),
      });
      return;
    }
    case "stopped":
      onEvent({
        type: "status",
        message: `Native executor stopped: ${event.reason}.`,
      });
  }
}

/**
 * Infer timeline status from a model-facing tool observation.
 *
 * Structured `ok: false` and `error` results are failures. Non-JSON output is
 * treated as successful because many established read tools return plain text.
 *
 * @param output - Bounded tool output replayed to the model.
 * @returns `true` when the observation does not explicitly report failure.
 */
function inferToolSuccess(output: string): boolean {
  try {
    const parsed = JSON.parse(output) as {
      ok?: unknown;
      error?: unknown;
    };
    return parsed.ok !== false && !parsed.error;
  } catch {
    return true;
  }
}
